import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  IconDefinition,
  findIconDefinition,
  library,
} from '@fortawesome/fontawesome-svg-core';
// Import Pro icons
import {
  faChevronLeft,
  faPuzzlePiece,
  faTableCells,
  faCalculator,
  faLayerGroup,
  faGear,
  faQuestionCircle,
  faArrowLeft,
  faSparkles,
  faDownload,
  faArrowUpRightFromSquare,
  faBullseyeArrow,
  faMemo,
  faSort,
  faSortDown,
  faSortUp,
  faUsers,
  faWaveformLines,
} from '@fortawesome/pro-regular-svg-icons';
// Import Pro thin icons
import {
  faTableCells as faTableCellsThin,
  faCalculator as faCalculatorThin,
  faGear as faGearThin,
} from '@fortawesome/pro-thin-svg-icons';

// Add icons to library
library.add(
  faChevronLeft,
  faPuzzlePiece,
  faTableCells,
  faCalculator,
  faLayerGroup,
  faGear,
  faQuestionCircle,
  faArrowLeft,
  faSparkles,
  faDownload,
  faArrowUpRightFromSquare,
  faBullseyeArrow,
  faMemo,
  faSort,
  faSortDown,
  faSortUp,
  faUsers,
  faWaveformLines,
  faTableCellsThin,
  faCalculatorThin,
  faGearThin
);

/**
 * Icon component for FontAwesome icons (FontAwesome 6 Pro)
 *
 * Supports:
 * - Class strings (e.g., 'fas fa-home', 'far fa-user', 'fa-thin fa-gear')
 * - FontAwesome IconDefinition objects
 *
 * For SSR compatibility with React 17, we use FontAwesome 6 packages.
 */
interface IconProps {
  icon: string | IconDefinition;
  className?: string;
  style?: React.CSSProperties;
}

// Map class strings to icon definitions for common icons
const iconMap: Record<string, IconDefinition> = {
  // Pro Regular icons
  'far fa-chevron-left': faChevronLeft,
  'far fa-puzzle-piece': faPuzzlePiece,
  'far fa-table-cells': faTableCells,
  'far fa-calculator': faCalculator,
  'far fa-layer-group': faLayerGroup,
  'far fa-gear': faGear,
  'far fa-cog': faGear, // alias
  'far fa-question-circle': faQuestionCircle,
  'far fa-arrow-left': faArrowLeft,
  'far fa-sparkles': faSparkles,
  // The two model families: a puzzle piece for a model that scores who a
  // company IS, a waveform for one that scores what they did.
  'far fa-waveform-lines': faWaveformLines,
  // Setup, matching the admin app's own glyphs for the same two screens, so a
  // person moving between the apps sees one language. Regular weight rather
  // than admin's solid, because this sidebar is outline throughout.
  'far fa-users': faUsers,
  'far fa-bullseye-arrow': faBullseyeArrow,
  // A written note. NOT a sparkle: these are notes a person typed, and the
  // sparkle is this product's mark for something a model generated.
  'far fa-memo': faMemo,
  'far fa-sort': faSort,
  'far fa-sort-up': faSortUp,
  'far fa-sort-down': faSortDown,
  // Pro Thin icons
  'fa-thin fa-table-cells': faTableCellsThin,
  'fa-thin fa-calculator': faCalculatorThin,
  'fa-thin fa-gear': faGearThin,
  'fa-thin fa-cog': faGearThin, // alias
};

export function Icon({ icon, className, style }: IconProps) {
  // If it's already an IconDefinition, use it directly
  if (typeof icon !== 'string') {
    return <FontAwesomeIcon icon={icon} className={className} style={style} />;
  }

  // Try to find icon in our map first
  let iconDefinition = iconMap[icon];

  // If not in map, try to find it in the library
  if (!iconDefinition) {
    const parts = icon.trim().split(/\s+/);
    let prefix: 'fas' | 'far' | 'fab' | 'fal' | 'fad' | 'fat' = 'far';
    let iconName = '';

    for (const part of parts) {
      if (part.startsWith('fa-') && !part.startsWith('fa-thin')) {
        iconName = part.replace('fa-', '');
      } else if (
        part === 'fas' ||
        part === 'far' ||
        part === 'fab' ||
        part === 'fal' ||
        part === 'fad'
      ) {
        prefix = part as 'fas' | 'far' | 'fab' | 'fal' | 'fad';
      } else if (part === 'fa-thin' || part === 'fat') {
        prefix = 'fat';
      }
    }

    if (iconName) {
      try {
        iconDefinition =
          findIconDefinition({
            prefix,
            iconName: iconName as any,
          }) || undefined;
      } catch {
        // Icon not found, will fallback to class string
      }
    }
  }

  if (iconDefinition) {
    return (
      <FontAwesomeIcon
        icon={iconDefinition}
        className={className}
        style={style}
      />
    );
  }

  // Fallback: render as class string (for icons not in library or custom icons)
  return <i className={`${icon} ${className || ''}`.trim()} style={style} />;
}
